import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { hasFlowAccess } from '../utils/authSession';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useTenant } from './TenantContext';

const PermissionsContext = createContext();

const emptyUser = { userId: null, email: null, name: null };

function readCachedProfile() {
    try {
        const raw = localStorage.getItem('userProfile');
        if (!raw) return emptyUser;
        const parsed = JSON.parse(raw);
        return {
            userId: parsed.userId || null,
            email: parsed.email || null,
            name: parsed.name || null,
        };
    } catch {
        return emptyUser;
    }
}

function readCachedTenantData() {
    try {
        const cached = localStorage.getItem('userTenantData');
        return cached ? JSON.parse(cached) : [];
    } catch {
        return [];
    }
}

export const PermissionsProvider = ({ children }) => {
    const { currentTenant } = useTenant();
    const [tenantData, setTenantData] = useState(readCachedTenantData);
    const [currentUser, setCurrentUser] = useState(readCachedProfile);
    const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);
    const [permissionsError, setPermissionsError] = useState(null);

    const fetchUserPermissions = useCallback(async ({ silent = false } = {}) => {
        const token = localStorage.getItem('token');

        if (!token) {
            setTenantData([]);
            setCurrentUser(emptyUser);
            setIsPermissionsLoading(false);
            return [];
        }

        try {
            if (!silent) {
                setIsPermissionsLoading(true);
            }
            const response = await fetchWithAuth(`${API_BASE_URL}/user/permissions`);

            if (response.ok) {
                const data = await response.json();
                const tenants = data.tenants || [];
                const profile = {
                    userId: data.userId || null,
                    email: data.email || null,
                    name: data.name || null,
                };
                setTenantData(tenants);
                setCurrentUser(profile);
                localStorage.setItem('userTenantData', JSON.stringify(tenants));
                localStorage.setItem('userProfile', JSON.stringify(profile));
                return tenants;
            }

            const cached = readCachedTenantData();
            setTenantData(cached);
            setCurrentUser(readCachedProfile());
            return cached;
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
            const cached = readCachedTenantData();
            setTenantData(cached);
            setCurrentUser(readCachedProfile());
            setPermissionsError(error.message);
            return cached;
        } finally {
            setIsPermissionsLoading(false);
        }
    }, []);

    // Derive roles and permissions for the active tenant without re-fetching
    const currentTenantEntry = useMemo(() => {
        const match = tenantData.find(t =>
            String(t.tenant_id) === String(currentTenant?.id ?? '')
        );
        return match ?? { roles: [], permissions: [] };
    }, [tenantData, currentTenant]);

    const roles = currentTenantEntry.roles;
    const permissions = currentTenantEntry.permissions;
    const hasApplicationAccess = useMemo(() => hasFlowAccess(tenantData), [tenantData]);

    const hasPermission = useCallback((requiredPermission) => {
        return permissions.includes(requiredPermission);
    }, [permissions]);

    const hasAnyPermission = useCallback((permissionList) => {
        return permissionList.some(perm => permissions.includes(perm));
    }, [permissions]);

    const hasAllPermissions = useCallback((permissionList) => {
        return permissionList.every(perm => permissions.includes(perm));
    }, [permissions]);

    const hasRole = useCallback((role_name) => {
        return roles.includes(role_name);
    }, [roles]);

    const clearPermissions = useCallback(() => {
        setTenantData([]);
        setCurrentUser(emptyUser);
        localStorage.removeItem('userTenantData');
        localStorage.removeItem('userProfile');
        // Remove legacy cache keys
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userRoles');
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            fetchUserPermissions();
        } else {
            setIsPermissionsLoading(false);
        }
    }, [fetchUserPermissions]);

    return (
        <PermissionsContext.Provider value={{
            userId: currentUser.userId,
            email: currentUser.email,
            name: currentUser.name,
            roles,
            permissions,
            tenantData,
            hasFlowAccess: hasApplicationAccess,
            isPermissionsLoading,
            permissionsError,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions,
            hasRole,
            fetchUserPermissions,
            clearPermissions
        }}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => useContext(PermissionsContext);

export default PermissionsContext;
